import React, { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Controller, EventFunction, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { Checkbox, FormControlLabel, Grid, InputLabel, Typography } from "@material-ui/core";
import { GlobalContext } from "globalContext/GlobalContext";
import moment from "moment";
import { CALCULATE_RATES, RoutesList, SAVE_NEW_RATE, UPDATE_EMPLOYEE_RATE } from "router/url";
import { KeyboardDatepickerInput, Modal, NumberInput } from "components";
import { datePickerDateFormat } from "utils/constants";
import { getDateFormatted } from "utils/dateFormatter";
import { useDebounce } from "utils/hooks/useDebounce";
import { useFetch } from "utils/hooks/useFetch";
import { EmploymentForm, employmentsOptions } from "../../constants";
import { IRate, ISelectOption } from "./RateForm.types";
import * as S from "./RateForm.css";

interface Props {
  onClose: () => void;
  getEmployeeList: () => void;
  getEmployeeDetails?: () => void;
  editedItem?: any; //TODO type
}

const defaultValues: IRate = {
  employeeHourlyRate: 0,
  employeeDailyRate: 0,
  monthlyGrossRate: 0,
  monthlyNetRate: 0,
  formOfEmployment: employmentsOptions[0],
  currency: { value: "PLN", label: "PLN" },
  student: false,
  clientDailyRate: 0,
  startingDate: new Date(),
  endDate: new Date(),
};

const RateForm = ({ onClose, getEmployeeList, getEmployeeDetails, editedItem }: Props) => {
  let { employeePendingLdapLogin } = useParams();

  const globalContext = useContext(GlobalContext);
  const { isLoading } = globalContext!;
  const currencyOptions = globalContext!.currencyOptions.map((el) => ({ label: el.name, value: el.name }));

  const initialStartingDate = editedItem ? moment(editedItem.dateFrom, "YYYY-MM-DD").toDate() : new Date();
  const initialEndDate = editedItem
    ? editedItem.dateTo === "9999-12-31"
      ? null
      : moment(editedItem.dateTo, "YYYY-MM-DD").toDate()
    : null;
  const formDefaultValues: IRate = editedItem
    ? {
        ...defaultValues,
        ...editedItem,
        formOfEmployment:
          employmentsOptions.find((item) => item.value === editedItem.formOfEmployment) ||
          defaultValues.formOfEmployment,
        currency: { value: editedItem.currency, label: editedItem.currency },
        employeeDailyRate: editedItem.dailyRate,
        employeeHourlyRate: editedItem.dailyRate / 8,
        startingDate: initialStartingDate,
        endDate: initialEndDate,
      }
    : defaultValues;

  const { handleSubmit, control, getValues, errors, setValue, watch } = useForm<IRate>({
    defaultValues: formDefaultValues,
  });

  const selectedCurrency = watch("currency");
  const selectedFormOfEmployment = watch("formOfEmployment", formDefaultValues.formOfEmployment);

  const [isStudentChecked, toggleStudentChecked] = useState(formDefaultValues.student);
  const [isPartTimeChecked, togglePartTimeChecked] = useState(false);

  const getAllCalculateRates = useFetch();

  const calculationRevisionRef = useRef(0);
  const lastSuccessfulCalculationRevisionRef = useRef(0);
  const isMountedRef = useRef(true);
  const [pendingRateCalculationRevision, setPendingRateCalculationRevision] = useState<number | null>(null);
  const invalidateRateCalculation = () => {
    calculationRevisionRef.current += 1;
    setPendingRateCalculationRevision(null);
    return calculationRevisionRef.current;
  };
  const scheduleRateCalculation = () => {
    calculationRevisionRef.current += 1;
    const nextRevision = calculationRevisionRef.current;
    setPendingRateCalculationRevision(nextRevision);
  };

  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(initialStartingDate);
  const selectedStartDateRef = useRef<Date | null>(initialStartingDate);
  const handleStartDateChange = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) {
      selectedStartDateRef.current = null;
      setSelectedStartDate(null);
      invalidateRateCalculation();
      return null;
    }

    selectedStartDateRef.current = date;
    setSelectedStartDate(date);
    scheduleRateCalculation();
    return date;
  };

  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(initialEndDate);
  const handleEndDateChange = (date: Date | null) => {
    setSelectedEndDate(date);
  };

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const calculateRates = useCallback(async (requestRevision: number) => {
    if (requestRevision !== calculationRevisionRef.current) {
      return;
    }

    const startingDate = selectedStartDateRef.current;
    if (!startingDate || Number.isNaN(startingDate.getTime())) {
      return;
    }

    try {
      const values = getValues();
      const payload = {
        employeeDailyRate: Number(values.employeeDailyRate),
        monthlyGrossRate: Number(values.monthlyGrossRate),
        monthlyNetRate: Number(values.monthlyNetRate),
        formOfEmployment: (values.formOfEmployment as ISelectOption)?.value,
        student: values.student,
        date: getDateFormatted(startingDate),
      };
      const response = await getAllCalculateRates("POST", CALCULATE_RATES, payload);
      if (!isMountedRef.current || requestRevision !== calculationRevisionRef.current) {
        return;
      }
      setValue("employeeHourlyRate", response.employeeDailyRate / 8 || null);
      setValue("employeeDailyRate", response.employeeDailyRate || null);
      setValue("monthlyGrossRate", response.monthlyGrossRate || null);
      setValue("monthlyNetRate", response.monthlyNetRate || null);
      lastSuccessfulCalculationRevisionRef.current = requestRevision;
    } catch (e) {
      if (isMountedRef.current && requestRevision === calculationRevisionRef.current) {
        toast.error("Rate calculation failed!");
      }
    }
  }, [getAllCalculateRates, getValues, setValue]);

  const delayedRateCalculationRevision = useDebounce(pendingRateCalculationRevision, 500);

  useEffect(() => {
    if (
      delayedRateCalculationRevision !== null &&
      delayedRateCalculationRevision === calculationRevisionRef.current
    ) {
      calculateRates(delayedRateCalculationRevision);
    }
  }, [calculateRates, delayedRateCalculationRevision]);

  const handleRateChange = ([e]: ChangeEvent<HTMLInputElement>[]) => {
    const value = e.target.value;
    const valueNumber = Number(value);
    const name = e.target.name;
    const isRateCalculationInput = ["employeeHourlyRate", "employeeDailyRate", "monthlyGrossRate"].includes(
      name
    );
    const isMonthlyEmploymentRate =
      name === "monthlyGrossRate" && selectedFormOfEmployment?.value === EmploymentForm.CONTRACT_OF_EMPLOYMENT;

    if (isMonthlyEmploymentRate) {
      setValue("employeeHourlyRate", null);
      setValue("employeeDailyRate", null);
    }

    if (value === "" || !Number.isFinite(valueNumber) || valueNumber <= 0) {
      setValue(name, null);
      if (isRateCalculationInput) {
        invalidateRateCalculation();
      }
      return e;
    }

    setValue(name, valueNumber);
    if (isRateCalculationInput) {
      scheduleRateCalculation();
    }
    return e;
  };

  const postNewRate = useFetch();
  const patchNewRate = useFetch();

  const onSubmit = handleSubmit(async (data: IRate) => {
    const employeeDailyRate = Number(data.employeeDailyRate);
    const startingDate = data.startingDate as Date;
    const endDateValue = data.endDate;
    const requiresCalculatedRates =
      selectedFormOfEmployment?.value === EmploymentForm.CIVIL_CONTRACT ||
      selectedFormOfEmployment?.value === EmploymentForm.CONTRACT_OF_EMPLOYMENT;

    if (
      requiresCalculatedRates &&
      calculationRevisionRef.current !== lastSuccessfulCalculationRevisionRef.current
    ) {
      toast.error("Rate must be recalculated before saving!");
      return;
    }

    if (!Number.isFinite(employeeDailyRate) || employeeDailyRate <= 0) {
      toast.error("Daily rate must be calculated before saving!");
      return;
    }

    const endDate = endDateValue ? { endDate: getDateFormatted(endDateValue) } : {};

    try {
      const payload = {
        clientUid: (data.clientId as any)?.uid,
        employeeDailyRate,
        pendingLdapLogin: employeePendingLdapLogin,
        formOfEmployment: (data.formOfEmployment as ISelectOption)?.value,
        monthlyGrossRate: data.monthlyGrossRate || 0,
        monthlyNetRate: data.monthlyNetRate || 0,
        clientDailyRate: data.clientDailyRate,
        currency: data.currency?.value,
        position: (data.positionId as any)?.uid,
        projectUid: (data.projectId as any)?.uid,
        startingDate: getDateFormatted(startingDate),
        ...endDate,
        student: isStudentChecked,
        partTimePercentage: data.partTimePercentage,
      };
      if (editedItem) {
        await patchNewRate("PATCH", UPDATE_EMPLOYEE_RATE(editedItem.id), payload);
      } else {
        await postNewRate("POST", SAVE_NEW_RATE, payload);
      }
      if (employeePendingLdapLogin && getEmployeeDetails) {
        getEmployeeDetails();
      }
      getEmployeeList();
      onClose();
      toast.success(
        <S.MessageLink to={`${RoutesList.EmployeeDetail}/${payload.pendingLdapLogin}`}>
          Rate has been saved! Click to open employee details.
        </S.MessageLink>
      );
    } catch (e) {
      toast.error("Rate has NOT been saved. Try again!");
    }
  });

  const onHourlyRateChange = ([e]: ChangeEvent<HTMLInputElement>[]) => {
    const employeeHourlyRate = Number(e.target.value);
    setValue("employeeDailyRate", employeeHourlyRate > 0 ? employeeHourlyRate * 8 : null);
    return handleRateChange([e]);
  };

  const onCurrencyChange: EventFunction = ([selected]) => {
    setValue("currency", selected);
    return selected;
  };

  const onStudentCheck: EventFunction = () => {
    const newState = !isStudentChecked;
    toggleStudentChecked(newState);
    setValue("student", newState);
    scheduleRateCalculation();
    return newState;
  };

  const onPartTimeChange: EventFunction = ([selected]) => {
    const newState = !isPartTimeChecked;
    togglePartTimeChecked(newState);
    return selected;
  };

  const onFormOfEmploymentChange: EventFunction = ([selected]) => {
    invalidateRateCalculation();
    setValue("employeeHourlyRate", null);
    setValue("employeeDailyRate", null);
    setValue("monthlyGrossRate", null);
    setValue("monthlyNetRate", null);
    setValue("student", false);
    toggleStudentChecked(false);
    return selected;
  };

  return (
    <Modal
      onClose={onClose}
      onSubmit={onSubmit}
      isLoading={isLoading}
      title={`${editedItem ? "Update" : "Add"} employee rate`}
      width={employeePendingLdapLogin ? undefined : "lg"}
    >
      <Grid container spacing={3}>
        <Grid item xs={employeePendingLdapLogin ? 12 : 6}>
          <S.FormGrid>
            {!employeePendingLdapLogin && <Typography>Rates</Typography>}
            <InputLabel>Form of employment</InputLabel>
            <Controller
              as={<Select />}
              name="formOfEmployment"
              control={control}
              options={employmentsOptions}
              onChange={onFormOfEmploymentChange}
            />
            <Controller
              as={
                <FormControlLabel
                  control={<Checkbox name="student" color="primary" />}
                  label="Is employee a student?"
                  disabled={
                    selectedFormOfEmployment?.value === EmploymentForm.B2B ||
                    selectedFormOfEmployment?.value === EmploymentForm.CONTRACT_OF_EMPLOYMENT
                  }
                />
              }
              onChange={onStudentCheck}
              name="student"
              control={control}
            />
            <Grid container xs={12} justify="space-between">
              {/* <Grid item xs={6}>
                <Controller
                  as={
                    <FormControlLabel
                      control={<Checkbox name="partTime" color="primary" />}
                      label="Part time?"
                      disabled={
                        rateCalculationInputData.formOfEmployment.value === EmploymentForm.B2B ||
                        rateCalculationInputData.formOfEmployment.value === EmploymentForm.CIVIL_CONTRACT
                      }
                    />
                  }
                  onChange={onPartTimeChange}
                  name="partTime"
                  control={control}
                />
              </Grid> */}
              {/* <Grid item xs={6}>
                <NumberInput
                  control={control}
                  name="partTimePercentage"
                  disabled={
                    rateCalculationInputData.formOfEmployment.value !== EmploymentForm.CONTRACT_OF_EMPLOYMENT ||
                    !isPartTimeChecked
                  }
                  label="Part time percentage [%]"
                  onChange={handleRateChange}
                  errors={errors}
                />
              </Grid> */}
              <Grid item xs={6}>
                <NumberInput
                  control={control}
                  name="employeeHourlyRate"
                  disabled={
                    selectedFormOfEmployment?.value === EmploymentForm.CIVIL_CONTRACT ||
                    selectedFormOfEmployment?.value === EmploymentForm.CONTRACT_OF_EMPLOYMENT
                  }
                  label="Hourly rate"
                  onChange={onHourlyRateChange}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  as={<Select />}
                  name="currency"
                  control={control}
                  label="Currency"
                  options={currencyOptions}
                  rules={{ required: false }}
                  defaultValue={currencyOptions.find((el) => el.label === "PLN")}
                  styles={{
                            menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        }}
                  menuPortalTarget={document.body}
                  onChange={onCurrencyChange}
                  value={selectedCurrency}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInput
                  control={control}
                  name="employeeDailyRate"
                  disabled={selectedFormOfEmployment?.value === EmploymentForm.CONTRACT_OF_EMPLOYMENT}
                  label="Daily rate"
                  onChange={handleRateChange}
                  required
                  errors={errors}
                />
              </Grid>

              <Grid item>
                <NumberInput
                  control={control}
                  name="monthlyGrossRate"
                  label="Monthly gross rate"
                  disabled={
                    selectedFormOfEmployment?.value === EmploymentForm.B2B ||
                    selectedFormOfEmployment?.value === EmploymentForm.CIVIL_CONTRACT
                  }
                  onChange={handleRateChange}
                  errors={errors}
                />
              </Grid>
              <Grid item>
                <NumberInput
                  control={control}
                  name="monthlyNetRate"
                  label="Monthly net rate"
                  disabled
                  errors={errors}
                />
              </Grid>
            </Grid>
            <KeyboardDatepickerInput
              format={datePickerDateFormat}
              margin="normal"
              control={control}
              name="startingDate"
              label="Starting date"
              onChange={handleStartDateChange}
              value={selectedStartDate}
              errors={errors}
              required
            />
            <KeyboardDatepickerInput
              format={datePickerDateFormat}
              margin="normal"
              control={control}
              name="endDate"
              label="End date"
              onChange={handleEndDateChange}
              value={selectedEndDate}
              errors={errors}
            />
            <Grid container xs={12} justify="space-between">
              <Grid item xs={6}>
                <Controller
                  as={
                    <FormControlLabel
                      control={<Checkbox name="partTime" color="primary" />}
                      label="Part time?"
                      disabled={
                        selectedFormOfEmployment?.value === EmploymentForm.B2B ||
                        selectedFormOfEmployment?.value === EmploymentForm.CIVIL_CONTRACT
                      }
                    />
                  }
                  onChange={onPartTimeChange}
                  name="partTime"
                  control={control}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInput
                  control={control}
                  name="partTimePercentage"
                  disabled={
                    selectedFormOfEmployment?.value !== EmploymentForm.CONTRACT_OF_EMPLOYMENT ||
                    !isPartTimeChecked
                  }
                  label="Part time percentage [%]"
                  onChange={handleRateChange}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </S.FormGrid>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default RateForm;
